import React, { useState, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { MonkeyLogo } from '../components/MonkeyLogo';
import { 
  Settings as SettingsIcon, LogOut, ExternalLink, Eye, MousePointerClick,
  Link as LinkIcon, Copy, Check, Shield, Bell, Newspaper, Plus, Loader2, X, Image, Globe, MapPin, Gift, Users, QrCode, ArrowRight, HelpCircle, Star
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../App';
import { api } from '../utils/api';
import { SubscribersCard } from '../components/SubscribersCard';
import EmailVerifyBanner from '../components/EmailVerifyBanner';
import QRCodeModal from '../components/QRCodeModal';
import DashboardTour from '../components/DashboardTour';
import ReviewDialog from '../components/ReviewDialog';

const Dashboard = () => {
  const navigate = useNavigate();
  const { user, logoutUser, refreshUser } = useAuth();
  const [copied, setCopied] = useState(false);
  const [tourActive, setTourActive] = useState(false);
  const [reviewOpen, setReviewOpen] = useState(false);
  const tourAutoRef = useRef(false);
  const [analytics, setAnalytics] = useState(null);
  const [hasUnread, setHasUnread] = useState(false);
  const [affiliateData, setAffiliateData] = useState(null);
  const [refCopied, setRefCopied] = useState(false);
  const [showQR, setShowQR] = useState(false);
  // Fan page quick post
  const [postText, setPostText] = useState('');
  const [postMediaUrl, setPostMediaUrl] = useState('');
  const [postImage, setPostImage] = useState(null);
  const [posting, setPosting] = useState(false);
  const postImageRef = useRef(null);

  useEffect(() => {
    if (user?.tour_completed === false && !tourAutoRef.current) {
      const t = setTimeout(() => {
        tourAutoRef.current = true;
        setTourActive(true);
      }, 800);
      return () => clearTimeout(t);
    }
  }, [user?.tour_completed]);

  useEffect(() => {
    api.getAnalytics().then(setAnalytics).catch(() => {});
    api.getAffiliateStatus().then(setAffiliateData).catch(() => {});
    const checkMessages = () => {
      api.getMessages().then(data => setHasUnread(data.has_unread || false)).catch(() => {});
    };
    checkMessages();
    const interval = setInterval(checkMessages, 15000);
    return () => clearInterval(interval);
  }, []);

  // Poll Stripe checkout status on return from Stripe
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get('session_id');
    if (!sessionId) return;

    let attempts = 0;
    const maxAttempts = 30; // ~60s total — covers Stripe redirect + webhook latency
    const intervalId = setInterval(async () => {
      attempts++;
      try {
        const data = await api.getCheckoutStatus(sessionId);
        if (data.fulfilled || data.payment_status === 'paid') {
          clearInterval(intervalId);
          toast.success('Welcome to Pro!');
          window.history.replaceState({}, '', '/dashboard');
          window.location.reload();
        } else if (data.status === 'expired' || attempts >= maxAttempts) {
          clearInterval(intervalId);
          if (data.status === 'expired') toast.error('Payment session expired');
          // If we ran out of attempts but Stripe never confirmed, the auto-heal
          // useEffect below will still run on the next page interaction.
        }
      } catch {
        if (attempts >= maxAttempts) clearInterval(intervalId);
      }
    }, 2000);

    return () => clearInterval(intervalId);
  }, []);

  // Self-heal Pro status on dashboard mount: if user looks Free but Stripe says
  // they have an active subscription on their email, silently sync. Covers
  // edge cases where the post-checkout polling missed (e.g. user closed the
  // Stripe tab too quickly, or the webhook arrived after the polling timed out).
  useEffect(() => {
    if (!user || user.plan === 'pro') return;
    let cancelled = false;
    (async () => {
      try {
        const res = await api.syncProFromStripe();
        if (cancelled) return;
        if (res?.ok && res?.plan) {
          toast.success("Welcome to Pro!");
          window.location.reload();
        }
      } catch { /* silent — user is genuinely free */ }
    })();
    return () => { cancelled = true; };
  }, [user]);

  const handleLogout = async () => {
    toast.success('Logged out successfully');
    navigate('/');
    await logoutUser();
  };

  const copyProfileLink = async () => {
    const profileUrl = `${window.location.origin}/${user?.username}`;
    try {
      await navigator.clipboard.writeText(profileUrl);
      setCopied(true);
      toast.success('Profile link copied!');
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Failed to copy link');
    }
  };

  const copyReferralLink = async () => {
    const refUrl = `${window.location.origin}/signup?ref=${user?.username}`;
    try {
      await navigator.clipboard.writeText(refUrl);
      setRefCopied(true);
      toast.success('Referral link copied!');
      setTimeout(() => setRefCopied(false), 2000);
    } catch {
      toast.error('Failed to copy link');
    }
  };

  const downloadQR = () => setShowQR(true);

  const handleQuickPost = async () => {
    if (!postText.trim() && !postMediaUrl.trim()) {
      toast.error('Add some text or a media link');
      return;
    }
    setPosting(true);
    try {
      const post = await api.createDiaryPost({
        text: postText.trim(),
        media_url: postMediaUrl.trim() || null
      });
      if (postImage && post.id) {
        await api.uploadDiaryImage(post.id, postImage);
      }
      setPostText('');
      setPostMediaUrl('');
      setPostImage(null);
      if (postImageRef.current) postImageRef.current.value = '';
      toast.success('Posted to your Fan Page!');
    } catch (err) { toast.error(err.message); }
    finally { setPosting(false); }
  };

  if (!user) return null;

  const linkCount = user.links?.length || 0;
  const profileUrl = `/${user.username}`;

  return (
    <div className="min-h-screen bg-[#D9E9FC] text-gray-950" data-testid="dashboard-page">
      <Helmet>
        <title>Dashboard - LinkMonkey</title>
      </Helmet>
      {/* Subtle blue radial wash for cinematic depth */}
      <div className="pointer-events-none fixed inset-0 -z-10" style={{ background: 'radial-gradient(ellipse 60% 50% at 80% 10%, rgba(0,85,255,0.10) 0%, transparent 60%)' }} />
      <nav className="bg-white/90 border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-2 min-w-0">
              <MonkeyLogo size={32} className="flex-shrink-0" />
              <span className="text-xl font-bold truncate">LinkMonkey</span>
            </div>
            <div className="flex items-center gap-1 sm:space-x-2 flex-shrink-0">
              {(user.role === 'admin' || user.email === 'admin@linkmonkey.io') && (
                <Link to="/admin">
                  <Button variant="ghost" size="sm" data-testid="admin-btn">
                    <Shield className="w-4 h-4 sm:mr-2" /> <span className="hidden sm:inline">Admin</span>
                  </Button>
                </Link>
              )}
              <Link to={profileUrl}>
                <Button variant="ghost" size="sm" data-testid="view-profile-btn">
                  <Eye className="w-4 h-4 sm:mr-2" /> <span className="hidden sm:inline">View Profile</span>
                </Button>
              </Link>
              <Button variant="ghost" size="icon" onClick={() => setReviewOpen(true)} title="Review LinkMonkey" data-testid="review-btn"><Star className="w-5 h-5" /></Button>
              <Button variant="ghost" size="icon" onClick={() => setTourActive(true)} title="Take the tour" data-testid="tour-btn"><HelpCircle className="w-5 h-5" /></Button>
              <Link to="/settings" className="relative" data-testid="notifications-btn">
                <Button variant="ghost" size="icon">
                  <Bell className="w-5 h-5" />
                  {hasUnread && (
                    <span className="absolute top-1 right-1 w-2.5 h-2.5 rounded-full bg-destructive border-2 border-background" data-testid="notification-dot" />
                  )}
                </Button>
              </Link>
              <Link to="/settings">
                <Button variant="ghost" size="icon" data-testid="settings-btn"><SettingsIcon className="w-5 h-5" /></Button>
              </Link>
              <Button variant="ghost" size="icon" onClick={handleLogout} data-testid="logout-btn"><LogOut className="w-5 h-5" /></Button>
            </div>
          </div>
        </div>
      </nav>
      <EmailVerifyBanner user={user} />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Plan Banner — Cinematic Cyber-Utility treatment, no purple slop */}
        {user.subscription?.is_pro ? (
          <div className="relative p-5 mb-6 rounded-2xl border border-[#0055FF]/25 bg-blue-50/60 overflow-hidden" data-testid="pro-banner">
            <div
              className="absolute -right-10 -top-10 w-64 h-64 pointer-events-none"
              style={{ background: 'radial-gradient(closest-side, rgba(0,85,255,0.12) 0%, transparent 70%)' }}
            />
            <div className="relative flex items-start justify-between gap-3 flex-wrap">
              <div className="flex items-start gap-3 min-w-0 flex-1">
                <div className="w-9 h-9 rounded-full bg-[#0055FF] flex items-center justify-center text-white text-[10px] font-bold tracking-wider flex-shrink-0 shadow-[0_8px_28px_rgba(0,85,255,0.55)]">PRO</div>
                <p className="text-sm min-w-0 break-words text-gray-800 font-body">
                  {user.subscription?.billing_cycle === 'grandfathered' ? (
                    <><strong className="text-gray-950">Lifetime Pro</strong> — thanks for being an early supporter. All features unlocked, forever.</>
                  ) : user.subscription?.billing_cycle === 'trial' && user.subscription?.ends_at ? (
                    <><strong className="text-gray-950">Pro Trial</strong> — {Math.max(0, Math.ceil((new Date(user.subscription.ends_at) - new Date()) / 86400000))} day(s) remaining. <Link to="/pricing" className="underline font-semibold text-[#0055FF] hover:text-[#3377FF] transition-colors">Upgrade to keep Pro →</Link></>
                  ) : user.subscription?.ends_at ? (
                    <>You're on <strong className="text-gray-950">Pro ({user.subscription.billing_cycle})</strong> — renews {new Date(user.subscription.ends_at).toLocaleDateString()}.</>
                  ) : (
                    <>You're on <strong className="text-gray-950">Pro</strong>. All features unlocked.</>
                  )}
                </p>
              </div>
              <Link to="/billing" className="flex-shrink-0">
                <button className="text-xs font-semibold uppercase tracking-wider px-4 py-2 rounded-full border border-gray-300 bg-white text-gray-900 hover:bg-[#C9DEF9] hover:border-gray-400 transition-colors" data-testid="banner-billing-btn">Manage billing</button>
              </Link>
            </div>
          </div>
        ) : (
          <div className="relative p-5 mb-6 rounded-2xl border border-amber-300 bg-amber-50/70 overflow-hidden" data-testid="upgrade-banner">
            <div
              className="absolute -right-10 -top-10 w-64 h-64 pointer-events-none"
              style={{ background: 'radial-gradient(closest-side, rgba(251,191,36,0.15) 0%, transparent 70%)' }}
            />
            <div className="relative flex items-center justify-between gap-3 flex-wrap">
              <p className="text-sm text-amber-900 font-body">
                You're on the <strong className="text-gray-950">Free plan</strong>. {!user.trial_used && 'Try Pro free for 7 days — no card required. '}Unlock all themes, Tip Jar, Fan Page, analytics and more.
              </p>
              <Link to="/pricing">
                <button className="text-xs font-semibold uppercase tracking-wider px-4 py-2 rounded-full bg-[#0055FF] text-white hover:bg-[#3377FF] transition-colors shadow-[0_8px_28px_rgba(0,85,255,0.45)]" data-testid="banner-upgrade-btn">
                  {!user.trial_used ? 'Start free trial' : 'Upgrade to Pro'}
                </button>
              </Link>
            </div>
          </div>
        )}

        <div className="mb-8 lm-enter">
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tighter mb-2 break-words text-gray-950" data-testid="welcome-heading">Welcome back, {user.name}.</h1>
          <p className="text-sm sm:text-base text-gray-500 font-body">Manage your links and track your performance.</p>
        </div>

        {/* Profile Link Card — Command Center surface */}
        <div className="p-5 sm:p-6 mb-8 rounded-2xl border border-gray-200 bg-white shadow-sm lm-enter" style={{ animationDelay: '60ms' }} data-testid="profile-link-card">
          <div className="flex flex-col gap-4">
            <div className="min-w-0">
              <p className="text-[11px] uppercase tracking-[0.22em] text-gray-500 mb-2 font-body">Your Profile Link</p>
              <div className="flex items-center gap-2 min-w-0">
                <code className="flex-1 min-w-0 text-sm sm:text-base font-display font-semibold tracking-tight bg-white border border-gray-200 text-gray-900 px-3.5 py-2.5 rounded-lg truncate" data-testid="profile-url">
                  linkmonkey.io/{user.username}
                </code>
                <button onClick={copyProfileLink} className="flex-shrink-0 w-10 h-10 inline-flex items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-500 hover:bg-[#C9DEF9] hover:text-gray-900 transition-colors" data-testid="copy-link-btn">
                  {copied ? <Check className="w-4 h-4 text-emerald-400" /> : <Copy className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-2 sm:flex sm:gap-2">
              <Link to={profileUrl} target="_blank" className="block">
                <button className="w-full sm:w-auto inline-flex items-center justify-center gap-2 text-xs font-semibold uppercase tracking-wider px-4 py-2.5 rounded-full border border-gray-300 bg-white text-gray-900 hover:bg-[#C9DEF9] hover:border-gray-400 transition-colors" data-testid="open-profile-btn"><ExternalLink className="w-4 h-4" /> <span className="hidden sm:inline">Open</span><span className="sm:hidden">Open</span></button>
              </Link>
              <button onClick={downloadQR} className="w-full sm:w-auto inline-flex items-center justify-center gap-2 text-xs font-semibold uppercase tracking-wider px-4 py-2.5 rounded-full border border-gray-300 bg-white text-gray-900 hover:bg-[#C9DEF9] hover:border-gray-400 transition-colors" data-testid="download-qr-btn">
                <QrCode className="w-4 h-4" /> <span className="hidden sm:inline">QR Code</span><span className="sm:hidden">QR</span>
              </button>
              <Link to="/settings" className="block">
                <button className="w-full sm:w-auto inline-flex items-center justify-center gap-2 text-xs font-semibold uppercase tracking-wider px-4 py-2.5 rounded-full bg-[#0055FF] text-white hover:bg-[#3377FF] transition-colors shadow-[0_8px_28px_rgba(0,85,255,0.45)]" data-testid="edit-profile-btn"><SettingsIcon className="w-4 h-4" /> <span className="hidden sm:inline">Edit Profile</span><span className="sm:hidden">Edit</span></button>
              </Link>
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8 lm-stagger" data-testid="stats-grid">
          <div className="relative p-6 rounded-2xl border border-gray-200 bg-white shadow-sm hover:border-gray-300 transition-colors group overflow-hidden" data-testid="stat-views">
            <div className="absolute inset-0 -z-0 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none" style={{ background: 'radial-gradient(closest-side at 100% 0%, rgba(0,85,255,0.18) 0%, transparent 70%)' }} />
            <div className="relative flex items-center justify-between mb-3">
              <p className="text-[11px] uppercase tracking-[0.22em] text-gray-500 font-body">Total Views</p>
              <Eye className="w-4 h-4 text-[#3377FF]" />
            </div>
            <p className="relative font-display text-4xl font-extrabold tracking-tighter tabular-nums text-gray-950">{analytics?.total_views ?? 0}</p>
            <p className="relative text-xs text-gray-500 mt-2 font-body">{analytics?.views_7d != null ? `${analytics.views_7d} this week` : ''}</p>
          </div>

          <div className="relative p-6 rounded-2xl border border-gray-200 bg-white shadow-sm hover:border-gray-300 transition-colors group overflow-hidden" data-testid="stat-clicks">
            <div className="absolute inset-0 -z-0 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none" style={{ background: 'radial-gradient(closest-side at 100% 0%, rgba(16,185,129,0.18) 0%, transparent 70%)' }} />
            <div className="relative flex items-center justify-between mb-3">
              <p className="text-[11px] uppercase tracking-[0.22em] text-gray-500 font-body">Total Clicks</p>
              <MousePointerClick className="w-4 h-4 text-emerald-400" />
            </div>
            <p className="relative font-display text-4xl font-extrabold tracking-tighter tabular-nums text-gray-950">{analytics?.total_clicks ?? 0}</p>
            <p className="relative text-xs text-gray-500 mt-2 font-body">{analytics?.clicks_7d != null ? `${analytics.clicks_7d} this week` : ''}</p>
          </div>

          <div className="relative p-6 rounded-2xl border border-gray-200 bg-white shadow-sm hover:border-gray-300 transition-colors group overflow-hidden" data-testid="stat-links">
            <div className="absolute inset-0 -z-0 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none" style={{ background: 'radial-gradient(closest-side at 100% 0%, rgba(244,114,182,0.18) 0%, transparent 70%)' }} />
            <div className="relative flex items-center justify-between mb-3">
              <p className="text-[11px] uppercase tracking-[0.22em] text-gray-500 font-body">Active Links</p>
              <LinkIcon className="w-4 h-4 text-pink-400" />
            </div>
            <p className="relative font-display text-4xl font-extrabold tracking-tighter tabular-nums text-gray-950">{linkCount}</p>
            <p className="relative text-xs text-gray-500 mt-2 font-body">{linkCount === 0 ? 'No links added yet' : 'Links visible on profile'}</p>
          </div>
        </div>

        {/* Content Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lm-stagger">
          {/* Quick Actions + Fan Page Post */}
          <div className="space-y-6">
            <div className="p-6 rounded-2xl border border-gray-200 bg-white shadow-sm" data-testid="recent-activity-card">
              <h3 className="font-display text-lg font-bold tracking-tight mb-4 text-gray-950">Quick Actions</h3>
              <div className="space-y-2">
                <Link to="/settings" className="block">
                  <div className="flex items-center p-3 rounded-lg bg-[#D9E9FC] hover:bg-[#C9DEF9] border border-gray-200 hover:border-gray-300 transition-colors">
                    <SettingsIcon className="w-4 h-4 text-[#3377FF] mr-3" /><p className="text-sm font-medium text-gray-800">Manage Links</p>
                  </div>
                </Link>
                <Link to={profileUrl} className="block">
                  <div className="flex items-center p-3 rounded-lg bg-[#D9E9FC] hover:bg-[#C9DEF9] border border-gray-200 hover:border-gray-300 transition-colors">
                    <Eye className="w-4 h-4 text-emerald-600 mr-3" /><p className="text-sm font-medium text-gray-800">Preview Profile</p>
                  </div>
                </Link>
                <Link to={`${profileUrl}/fanpage`} className="block">
                  <div className="flex items-center p-3 rounded-lg bg-[#D9E9FC] hover:bg-[#C9DEF9] border border-gray-200 hover:border-gray-300 transition-colors">
                    <Newspaper className="w-4 h-4 text-orange-500 mr-3" /><p className="text-sm font-medium text-gray-800">View Fan Page</p>
                  </div>
                </Link>
              </div>
            </div>

            {/* Quick Fan Page Post */}
            <div className="p-6 rounded-2xl border border-gray-200 bg-white shadow-sm" data-testid="quick-post-card">
              <div className="flex items-center gap-2 mb-4">
                <Newspaper className="w-4 h-4 text-[#3377FF]" />
                <h3 className="font-display text-lg font-bold tracking-tight text-gray-950">Quick Post</h3>
              </div>
              <Textarea
                value={postText}
                onChange={(e) => setPostText(e.target.value)}
                placeholder="Share a thought, video, or song..."
                className="min-h-[70px] mb-3 bg-white border-gray-300 text-gray-900 placeholder:text-gray-400 focus-visible:ring-0 focus-visible:border-[#0055FF]"
                maxLength={500}
                data-testid="quick-post-text"
              />
              <div className="flex flex-wrap items-center gap-2 mb-3">
                <Input
                  value={postMediaUrl}
                  onChange={(e) => setPostMediaUrl(e.target.value)}
                  placeholder="YouTube, Spotify, SoundCloud link"
                  className="h-9 text-sm flex-1 min-w-[200px] bg-white border-gray-300 text-gray-900 placeholder:text-gray-400 focus-visible:ring-0 focus-visible:border-[#0055FF]"
                  data-testid="quick-post-media"
                />
                <button onClick={() => postImageRef.current?.click()} className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider px-3 py-2 rounded-full border border-gray-300 bg-white text-gray-900 hover:bg-[#C9DEF9] transition-colors" data-testid="quick-post-image-btn">
                  <Image className="w-4 h-4" /> {postImage ? 'Change' : 'Image'}
                </button>
                {postImage && (
                  <button onClick={() => { setPostImage(null); if (postImageRef.current) postImageRef.current.value = ''; }} className="inline-flex items-center gap-1 text-xs text-gray-600 hover:text-gray-900 px-2">
                    <X className="w-3 h-3" /> {postImage.name.slice(0, 12)}
                  </button>
                )}
                <input ref={postImageRef} type="file" accept="image/jpeg,image/png,image/gif,image/webp" className="hidden"
                  onChange={(e) => { if (e.target.files[0]) setPostImage(e.target.files[0]); }} />
              </div>
              <div className="flex justify-between items-center">
                <span className="text-xs text-gray-500 font-body">{postText.length}/500</span>
                <button onClick={handleQuickPost} disabled={posting || (!postText.trim() && !postMediaUrl.trim())} className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider px-4 py-2 rounded-full bg-[#0055FF] text-white hover:bg-[#3377FF] disabled:opacity-40 disabled:cursor-not-allowed transition-colors shadow-[0_8px_28px_rgba(0,85,255,0.45)]" data-testid="quick-post-btn">
                  {posting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Post
                </button>
              </div>
            </div>
          </div>

          <div className="p-6 rounded-2xl border border-gray-200 bg-white shadow-sm" data-testid="your-links-card">
            <h3 className="font-display text-lg font-bold tracking-tight mb-4 text-gray-950">Your Links ({linkCount})</h3>
            {linkCount === 0 ? (
              <div className="text-center py-6">
                <img
                  src="/mascot-empty-state.png"
                  alt="LinkMonkey says: add your first link"
                  className="w-32 h-32 mx-auto mb-3 drop-shadow-[0_10px_30px_rgba(0,85,255,0.25)] select-none animate-[fade-in_0.6s_ease-out]"
                  data-testid="empty-state-monkey"
                  draggable={false}
                />
                <p className="font-display text-base font-bold text-gray-950 mb-1">Your stage is empty.</p>
                <p className="text-sm text-gray-500 mb-4 font-body">Add your first link and your fans will find every platform you're on.</p>
                <Link to="/settings"><button className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider px-4 py-2 rounded-full bg-[#0055FF] text-white hover:bg-[#3377FF] transition-colors shadow-[0_8px_28px_rgba(0,85,255,0.45)]" data-testid="add-first-link-btn">Add Your First Link</button></Link>
              </div>
            ) : (
              <div className="space-y-2">
                {user.links.slice(0, 5).map((link) => (
                  <div key={link.id} className="flex items-center justify-between p-3 rounded-lg bg-[#D9E9FC] border border-gray-200 hover:bg-[#C9DEF9] hover:border-gray-300 transition-colors">
                    <div className="flex items-center space-x-3 min-w-0">
                      <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${link.color}22`, border: `1px solid ${link.color}44` }}>
                        <LinkIcon className="w-4 h-4" style={{ color: link.color }} />
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-gray-950 truncate">{link.title}</p>
                        {analytics?.link_clicks?.[link.id] != null && (
                          <p className="text-xs text-gray-500 font-body">{analytics.link_clicks[link.id]} clicks</p>
                        )}
                      </div>
                    </div>
                    <span className="text-[10px] uppercase tracking-wider font-semibold px-2 py-1 rounded-full bg-[#0055FF]/10 border border-[#0055FF]/30 text-[#3377FF] flex-shrink-0">{link.platformName}</span>
                  </div>
                ))}
                {linkCount > 5 && (
                  <Link to="/settings"><p className="text-sm text-[#0055FF] text-center hover:text-[#3377FF] transition-colors mt-2 font-body">View all {linkCount} links →</p></Link>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Visitor Map */}
        <div className="p-6 mt-6 rounded-2xl border border-gray-200 bg-white shadow-sm" data-testid="visitor-map-card">
          <div className="flex items-center gap-2 mb-4">
            <Globe className="w-4 h-4 text-[#3377FF]" />
            <h3 className="font-display text-lg font-bold tracking-tight text-gray-950">Visitor Map</h3>
          </div>
          <p className="text-sm text-gray-500 mb-4 font-body">See where your profile visitors are coming from</p>
          {(!analytics?.visitor_countries || analytics.visitor_countries.length === 0) ? (
            <div className="text-center py-8" data-testid="visitor-map-empty">
              <MapPin className="w-10 h-10 mx-auto mb-3 text-gray-300" />
              <p className="text-gray-500 font-body">No visitor data yet. Share your profile to start tracking!</p>
            </div>
          ) : (
            <div className="space-y-2" data-testid="visitor-map-list">
              {analytics.visitor_countries.map((item, i) => {
                const maxViews = analytics.visitor_countries[0]?.views || 1;
                const pct = Math.round((item.views / maxViews) * 100);
                const flag = item.country ? String.fromCodePoint(...[...item.country.toUpperCase()].map(c => 0x1F1E6 + c.charCodeAt(0) - 65)) : '';
                return (
                  <div key={item.country} className="flex items-center gap-3" data-testid={`visitor-country-${item.country}`}>
                    <span className="text-xl w-8 text-center flex-shrink-0">{flag}</span>
                    <span className="w-10 text-xs font-mono text-gray-500 flex-shrink-0">{item.country}</span>
                    <div className="flex-1 h-7 bg-[#C9DEF9] border border-blue-100 rounded-full overflow-hidden relative">
                      <div
                        className="h-full rounded-full transition-all duration-500"
                        style={{
                          width: `${Math.max(pct, 4)}%`,
                          background: i === 0 ? '#0055FF' : i === 1 ? 'rgba(0,85,255,0.6)' : 'rgba(0,85,255,0.35)'
                        }}
                      />
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-medium text-gray-700 font-body">
                        {item.views} {item.views === 1 ? 'visit' : 'visits'}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Email Subscribers */}
        <SubscribersCard />

        {/* Affiliate Program — open to everyone */}
        {affiliateData?.is_affiliate && (
          <div className="relative p-6 mt-6 rounded-2xl border border-[#0055FF]/20 bg-white shadow-sm overflow-hidden" data-testid="affiliate-card">
            <div
              className="absolute -left-10 -bottom-10 w-64 h-64 pointer-events-none"
              style={{ background: 'radial-gradient(closest-side, rgba(0,85,255,0.18) 0%, transparent 70%)' }}
            />
            <div className="relative">
              <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
                <div className="flex items-center gap-2">
                  <Gift className="w-5 h-5 text-[#3377FF]" />
                  <h3 className="font-display text-lg font-bold tracking-tight text-gray-950">Affiliate Program</h3>
                </div>
                <Link to="/affiliate/dashboard">
                  <button className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider px-4 py-2 rounded-full bg-[#0055FF] text-white hover:bg-[#3377FF] transition-colors shadow-[0_8px_28px_rgba(0,85,255,0.45)]" data-testid="view-affiliate-dashboard-btn">
                    View earnings <ArrowRight className="w-4 h-4" />
                  </button>
                </Link>
              </div>
              <p className="text-sm text-gray-600 mb-4 font-body">
                Earn <strong className="text-gray-950">€1</strong> per monthly Pro signup and <strong className="text-gray-950">€10</strong> per yearly Pro signup — recurring for as long as they stay subscribed.
              </p>

              <div className="p-4 rounded-xl bg-[#D9E9FC] border border-gray-200">
                <p className="text-[11px] uppercase tracking-[0.22em] text-gray-500 mb-2 font-body">Your referral link</p>
                <div className="flex items-center gap-2">
                  <code className="text-sm font-display font-semibold bg-white border border-gray-200 text-gray-900 px-3 py-2 rounded flex-1 truncate" data-testid="affiliate-link">
                    linkmonkey.io/signup?ref={user.username}
                  </code>
                  <button onClick={copyReferralLink} className="w-10 h-10 inline-flex items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-500 hover:bg-[#C9DEF9] hover:text-gray-900 transition-colors" data-testid="copy-affiliate-btn">
                    {refCopied ? <Check className="w-4 h-4 text-emerald-400" /> : <Copy className="w-4 h-4" />}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      <QRCodeModal open={showQR} onClose={() => setShowQR(false)} username={user.username} />
      <DashboardTour active={tourActive} onClose={() => { setTourActive(false); refreshUser().catch(() => {}); }} />
      <ReviewDialog open={reviewOpen} onClose={() => setReviewOpen(false)} />
    </div>
  );
};

export default Dashboard;
